/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file MailFolderManagerSheet.js
 * @description
 * Premium bottom sheet to create, rename / recolour and delete CUSTOM mailbox folders
 * (`POST/PUT/DELETE /api/mail/folders`). Built-in folders (Inbox, Sent, Drafts, Archive,
 * Spam, Trash) are never listed. The folder id is derived by the backend from the first
 * name, so a rename only changes the display name — mail already filed in the folder
 * stays there. Deleting asks for confirmation through {@link HappyAlert}; a duplicate
 * name (HTTP 409) is surfaced with a localized message.
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ActivityIndicator, StyleSheet, Text, TextInput, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import Toast from 'react-native-toast-message';
import Icon from '../management/SafeIcon';
import HappyAlert from '../HappyAlert';
import {PremiumSheet} from '../settings/SettingsUiKit';
import {useManagementTheme} from '../../helpers/managementTheme';
import {useMail} from '../../helpers/MailContext';
import {createMailFolder, updateMailFolder, deleteMailFolder} from '../../services/mailApi';
import {MAIL_COLOR_IDS, colorHex} from '../../helpers/mailOrganize';

/** Standard folder ids/names a custom folder may never shadow (backend rejects them with 409). */
const RESERVED = ['inbox', 'sent', 'archive', 'queued', 'drafts', 'spam', 'trash'];

/**
 * Folder manager bottom sheet.
 * @param {Object} props
 * @param {boolean} props.visible Sheet visibility.
 * @param {() => void} props.onClose Dismiss handler.
 * @param {(deletedId?: string) => void} [props.onChanged] Called after any successful mutation
 *   (the deleted folder id is passed on delete so the caller can leave a folder that vanished).
 * @returns {JSX.Element}
 */
const MailFolderManagerSheet = ({visible, onClose, onChanged}) => {
    const {t} = useTranslation();
    const m = useManagementTheme();
    const mail = useMail();
    const {refreshFolders} = mail;

    const custom = useMemo(
        () => (Array.isArray(mail.directories) ? mail.directories : [])
            .filter((d) => d && !d.system && !RESERVED.includes(String(d.id || '').toLowerCase())),
        [mail.directories],
    );

    const [editingId, setEditingId] = useState(null);   // null = creating a new folder
    const [name, setName] = useState('');
    const [color, setColor] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (visible) {
            setEditingId(null);
            setName('');
            setColor('');
            setBusy(false);
            refreshFolders();
        }
        // Reset once per opening only.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    /** Map a failed request to a localized toast (409 = duplicate / reserved name). */
    const toastError = useCallback((e) => {
        Toast.show({
            type: 'error',
            text1: e?.status === 409 ? t('mail_folder_exists') : t('mail_folder_save_failed'),
            text2: e?.status === 409 ? undefined : e?.message,
        });
    }, [t]);

    const startEdit = useCallback((dir) => {
        setEditingId(dir.id);
        setName(dir.name || dir.id || '');
        setColor(dir.color || '');
    }, []);

    const resetForm = useCallback(() => {
        setEditingId(null);
        setName('');
        setColor('');
    }, []);

    const save = useCallback(async () => {
        const clean = name.trim();
        if (!clean || busy) {
            return;
        }
        setBusy(true);
        try {
            if (editingId) {
                await updateMailFolder(editingId, {name: clean, color});
                Toast.show({type: 'success', text1: t('mail_folder_renamed')});
            } else {
                await createMailFolder({name: clean, color});
                Toast.show({type: 'success', text1: t('mail_folder_created')});
            }
            resetForm();
            await refreshFolders();
            if (onChanged) {
                onChanged();
            }
        } catch (e) {
            toastError(e);
        } finally {
            setBusy(false);
        }
    }, [name, color, busy, editingId, t, resetForm, refreshFolders, onChanged, toastError]);

    const confirmDelete = useCallback((dir) => {
        HappyAlert.show({
            type: 'question',
            title: t('mail_folder_delete_title', {name: dir.name || dir.id}),
            message: t('mail_folder_delete_message'),
            buttons: [
                {text: t('cancel'), style: 'cancel'},
                {
                    text: t('mail_remove'),
                    style: 'destructive',
                    onPress: async () => {
                        try {
                            await deleteMailFolder(dir.id);
                            Toast.show({type: 'success', text1: t('mail_folder_deleted')});
                            if (editingId === dir.id) {
                                resetForm();
                            }
                            await refreshFolders();
                            if (onChanged) {
                                onChanged(dir.id);
                            }
                        } catch (e) {
                            toastError(e);
                        }
                    },
                },
            ],
        });
    }, [t, editingId, resetForm, refreshFolders, onChanged, toastError]);

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('mail_folders_manage')}
            subtitle={t('mail_folders_manage_sub')}
            cancelText={t('mail_cancel')}
        >
            {custom.length === 0 ? (
                <Text style={[styles.empty, {color: m.colors.muted}]}>{t('mail_folders_none')}</Text>
            ) : custom.map((dir) => (
                <View key={dir.id} style={[styles.row, {borderBottomColor: m.colors.borderSoft}]}>
                    <View style={[styles.dot, {backgroundColor: dir.color || m.colors.muted}]} />
                    <Text style={[styles.rowName, {color: m.colors.heading}]} numberOfLines={1}>
                        {dir.name || dir.id}
                    </Text>
                    <TouchableOpacity onPress={() => startEdit(dir)} hitSlop={8} style={styles.iconBtn} accessibilityLabel={t('mail_edit')}>
                        <Icon name="pencil-outline" size={18} color={m.colors.primary} />
                    </TouchableOpacity>
                    <TouchableOpacity onPress={() => confirmDelete(dir)} hitSlop={8} style={styles.iconBtn} accessibilityLabel={t('mail_remove')}>
                        <Icon name="trash-can-outline" size={18} color={m.colors.danger} />
                    </TouchableOpacity>
                </View>
            ))}

            <Text style={[styles.sectionTitle, {color: m.colors.muted}]}>
                {editingId ? t('mail_folder_rename') : t('mail_folder_new')}
            </Text>
            <TextInput
                value={name}
                onChangeText={setName}
                placeholder={t('mail_folder_name_placeholder')}
                placeholderTextColor={m.colors.muted}
                maxLength={60}
                autoCapitalize="sentences"
                returnKeyType="done"
                onSubmitEditing={save}
                style={[styles.input, {color: m.colors.heading, backgroundColor: m.colors.surfaceAlt, borderColor: m.colors.border}]}
            />
            <View style={styles.swatchRow}>
                <TouchableOpacity
                    onPress={() => setColor('')}
                    activeOpacity={0.85}
                    style={[styles.swatch, {borderColor: color === '' ? m.colors.primary : m.colors.border, backgroundColor: m.colors.surfaceAlt}]}
                >
                    <Icon name="close" size={14} color={m.colors.muted} />
                </TouchableOpacity>
                {MAIL_COLOR_IDS.map((id) => {
                    const hex = colorHex(id, m.isDark);

                    return (
                        <TouchableOpacity
                            key={id}
                            onPress={() => setColor(hex)}
                            activeOpacity={0.85}
                            style={[styles.swatch, {backgroundColor: hex, borderColor: color === hex ? m.colors.heading : 'transparent'}]}
                        >
                            {color === hex && <Icon name="check" size={14} color="#FFFFFF" />}
                        </TouchableOpacity>
                    );
                })}
            </View>
            <View style={styles.actions}>
                {!!editingId && (
                    <TouchableOpacity onPress={resetForm} activeOpacity={0.85} style={[styles.btn, {borderColor: m.colors.border, backgroundColor: m.colors.surface}]}>
                        <Text style={[styles.btnText, {color: m.colors.heading}]}>{t('mail_cancel')}</Text>
                    </TouchableOpacity>
                )}
                <TouchableOpacity
                    onPress={save}
                    disabled={busy || !name.trim()}
                    activeOpacity={0.85}
                    style={[styles.btn, {backgroundColor: m.colors.primary, borderColor: m.colors.primary, opacity: busy || !name.trim() ? 0.5 : 1}]}
                >
                    {busy
                        ? <ActivityIndicator size="small" color="#FFFFFF" />
                        : <Text style={[styles.btnText, {color: '#FFFFFF'}]}>{editingId ? t('mail_save') : t('mail_folder_create')}</Text>}
                </TouchableOpacity>
            </View>
        </PremiumSheet>
    );
};

const styles = StyleSheet.create({
    empty: {fontSize: 13, fontWeight: '600', marginBottom: 6},
    row: {flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth},
    dot: {width: 12, height: 12, borderRadius: 6, marginRight: 10},
    rowName: {flex: 1, fontSize: 15, fontWeight: '800'},
    iconBtn: {width: 34, height: 34, alignItems: 'center', justifyContent: 'center', marginLeft: 4},
    sectionTitle: {fontSize: 11.5, fontWeight: '900', letterSpacing: 0.6, textTransform: 'uppercase', marginTop: 16, marginBottom: 8},
    input: {borderWidth: 1, borderRadius: 14, paddingHorizontal: 14, paddingVertical: 11, fontSize: 15, fontWeight: '600'},
    swatchRow: {flexDirection: 'row', flexWrap: 'wrap', marginTop: 12},
    swatch: {width: 30, height: 30, borderRadius: 15, borderWidth: 2, alignItems: 'center', justifyContent: 'center', marginRight: 10, marginBottom: 8},
    actions: {flexDirection: 'row', justifyContent: 'flex-end', marginTop: 6},
    btn: {minWidth: 110, height: 44, paddingHorizontal: 18, borderRadius: 22, borderWidth: 1, alignItems: 'center', justifyContent: 'center', marginLeft: 10},
    btnText: {fontSize: 14, fontWeight: '900'},
});

export default MailFolderManagerSheet;
